import type { XoLang } from "./xoDemoContext";

export type XoDemoCopy = {
  languageToggle: string;
  flow: {
    label: string;
    confirmOptional: string;
  };
  landing: {
    eyebrow: string;
    hero: string;
    subline: string;
    frictionLine: string;
    primaryCta: string;
    secondaryCta: string;
    returnLine: string;
    whyAnchor: string;
    flowTitle: string;
    flowSubtitle: string;
    steps: ReadonlyArray<{ n: string; title: string; line: string; hint: string; optional?: boolean }>;
    exampleTitle: string;
    exampleParagraphs: string[];
    whyTitle: string;
    whyBullets: string[];
    whereTitle: string;
    whereLine: string;
    domains: ReadonlyArray<{ title: string; body: string }>;
  };
  demo: {
    kicker: string;
    seedChip: string;
    hero: string;
    subline: string;
    frictionLine: string;
    primaryCta: string;
    secondaryCta: string;
    returnLine: string;
    whyAnchor: string;
    benefitsAria: string;
    benefits: ReadonlyArray<{ title: string; body: string }>;
  };
  callSetup: {
    prepareTitle: string;
    prepareBody: string;
    chooseHandle: string;
    preparing: string;
    prepareButton: string;
    serviceConnected: string;
    guestPrepare: string;
    guestInvited: string;
    setupSteps: string;
    step1Ready: string;
    step2Start: string;
    joinTitle: string;
    joining: string;
    joinButton: string;
    checkingService: string;
    serviceUnavailable: string;
    joinedLabel: string;
    roomLabel: string;
    callLabel: string;
    sessionLabel: string;
    youLabel: string;
    startBusy: string;
    startButton: string;
    liveTitle: string;
  };
  callCompletion: {
    viewRecord: string;
    continueLine: string;
    capturedConfirmed: string;
    capturedNotConfirmed: string;
  };
  record: {
    heroTitle: string;
    heroSubline: string;
    statusComplete: string;
    statusMissing: string;
    statusCaptured: string;
  };
  trustLoop: {
    title: string;
    subline: string;
  };
  fallback: {
    mediaUnavailable: string;
    retryVideo: string;
    continueWithoutVideo: string;
    technicalDetails: string;
  };
};

const xoCopyEnglish: XoDemoCopy = {
    languageToggle: "NL",
    flow: {
      label: "Talk → Capture → Review → Confirm when agreed",
      confirmOptional: "(when agreed)",
    },
    landing: {
      eyebrow: "Talk → Capture → Review → Confirm when agreed",
      hero: "Capture what you agree — so it stays clear later.",
      subline: "From conversation to shared agreement. Nothing is final until you confirm it together.",
      frictionLine: "You only use this when it matters — everything else stays the same.",
      primaryCta: "Try a call",
      secondaryCta: "View example",
      returnLine: "You’ll return with a shared agreement after the call.",
      whyAnchor: "This doesn’t replace conversations — it makes sure they still hold later.",
      flowTitle: "How it works",
      flowSubtitle: "Talk → Capture → Review → Confirm when agreed",
      steps: [
        { n: "01", title: "Talk", line: "Have the conversation as you normally would.", hint: "No new working style required." },
        { n: "02", title: "Capture", line: "What’s discussed is captured into a shared agreement.", hint: "Nothing is final yet." },
        { n: "03", title: "Review", line: "Both sides see the same version.", hint: "Review before anything is final." },
        { n: "04", title: "Confirm", line: "Only when both agree, it becomes final.", hint: "Confirm only when it is correct.", optional: true },
      ],
      exampleTitle: "Example: a clear agreement after a call",
      exampleParagraphs: [
        "Two people agree on when work will be done.",
        "After the call, it’s clearly captured in one shared agreement.",
        "Both review the same version and confirm when it’s correct. No loose messages or interpretation later.",
      ],
      whyTitle: "Why this helps",
      whyBullets: [
        "No ‘I thought…’ later",
        "No scattered notes or screenshots",
        "One shared version of truth",
        "Easy handover without losing context",
      ],
      whereTitle: "Where this helps",
      whereLine: "For municipalities, project teams, and handovers where clarity matters later.",
      domains: [
        { title: "Municipality", body: "Capture agreements and next steps calmly." },
        { title: "Handovers", body: "Keep context available for the next person." },
        { title: "Project management", body: "Read back what was discussed, decided, and still open." },
        { title: "Collaboration", body: "Avoid follow-up friction without extra weight." },
      ],
    },
    demo: {
      kicker: "Shared agreement",
      seedChip: "Example ready",
      hero: "Capture what you agree — so it stays clear later.",
      subline: "From conversation to shared agreement. Nothing is final until you confirm it together.",
      frictionLine: "You only use this when it matters — everything else stays the same.",
      primaryCta: "Try a call",
      secondaryCta: "View example",
      returnLine: "You’ll return with a shared agreement after the call.",
      whyAnchor: "This doesn’t replace conversations — it makes sure they still hold later.",
      benefitsAria: "Shared agreement benefits",
      benefits: [
        { title: "Fewer disputes later", body: "Less room for interpretation later." },
        { title: "Clear what’s agreed and what’s not", body: "You can see what is agreed and what still needs alignment." },
      ],
    },
    callSetup: {
      prepareTitle: "Prepare this browser",
      prepareBody: "This prepares this browser for shared call rooms.",
      chooseHandle: "Choose your handle",
      preparing: "Preparing…",
      prepareButton: "Prepare this browser",
      serviceConnected: "Call service connected",
      guestPrepare: "Join this room after preparing your browser.",
      guestInvited: "You have been invited to a shared call room.",
      setupSteps: "Three steps: prepare, start, capture the agreement.",
      step1Ready: "Step 1 — Prepare: already satisfied on this device (browser identity is ready).",
      step2Start: "Step 2 — Start the room when both sides are ready.",
      joinTitle: "Join call room",
      joining: "Joining…",
      joinButton: "Join call room",
      checkingService: "Checking call service…",
      serviceUnavailable: "Call service unavailable.",
      joinedLabel: "Joined call room",
      roomLabel: "Room",
      callLabel: "Call",
      sessionLabel: "Session",
      youLabel: "You",
      startBusy: "Starting…",
      startButton: "Start call room",
      liveTitle: "Call room live",
    },
    callCompletion: {
      viewRecord: "View your record",
      continueLine: "Continue from call to record.",
      capturedConfirmed: "This record is captured and confirmed.",
      capturedNotConfirmed: "This record is captured, but not confirmed yet.",
    },
    record: {
      heroTitle: "A record of what happened — ready when you are",
      heroSubline: "Captured from the call. Nothing is final unless you choose to seal it.",
      statusComplete: "This record is captured and confirmed.",
      statusMissing: "This record still needs alignment before review.",
      statusCaptured: "This record is captured, but not confirmed yet.",
    },
    trustLoop: {
      title: "From call to record — one clear step at a time",
      subline: "Start with a conversation. What follows can be reviewed together and confirmed only when it’s agreed.",
    },
    fallback: {
      mediaUnavailable: "Live video could not connect. You can continue without video and keep capturing the shared agreement.",
      retryVideo: "Retry video",
      continueWithoutVideo: "Continue without video",
      technicalDetails: "Technical details",
    },
  };

export const xoDemoCopy: Record<XoLang, XoDemoCopy> = {
  en: xoCopyEnglish,
  nl: xoCopyEnglish,
};
